import { Box, Link, Typography } from "@mui/material";

import { EndorsementEligibilityBlockReason } from "@covertree/agent-portal-graphql";
import { Button, Modal } from "@covertree/ui";

import {
  AGENT_HELP_ADDRESS,
  POLICY_CLOSE_EXPIRATION_ENDORSEMENT_MSG,
} from "@consts/index";

interface EndorsementIneligibleModalProps {
  readonly isModalOpen: boolean;
  readonly reason: EndorsementEligibilityBlockReason | "Unknown";
  readonly onClose: () => void;
}

const reasonMessages: Record<
  EndorsementEligibilityBlockReason | "Unknown",
  { title: string; description: string; shallShowContactDetails: boolean }
> = {
  [EndorsementEligibilityBlockReason.PolicyExpired]: {
    title: "Policy expired",
    description: "Policy expired - new endorsements are unavailable.",
    shallShowContactDetails: true,
  },
  [EndorsementEligibilityBlockReason.ActiveRenewal]: {
    title: "Renewal in Progress",
    description:
      "This action cannot be completed while a renewal is in progress. Please come back once the renewal has been issued.",
    shallShowContactDetails: false,
  },
  [EndorsementEligibilityBlockReason.NonRenewalIssued]: {
    title: "Non-Renewal Exists",
    description:
      "This change cannot be made as a non-renewal exists for this policy.",
    shallShowContactDetails: true,
  },
  [EndorsementEligibilityBlockReason.PolicyCancelled]: {
    title: "Cancellation Exists",
    description:
      "This policy has an active cancellation. This action cannot be completed.",
    shallShowContactDetails: true,
  },
  [EndorsementEligibilityBlockReason.PolicyCloseExpiration]: {
    title: "Endorsements Unavailable",
    description: POLICY_CLOSE_EXPIRATION_ENDORSEMENT_MSG,
    shallShowContactDetails: true,
  },
  Unknown: {
    title: "Something Went Wrong",
    description:
      "Sorry, something went wrong and a new endorsement can't be created for this policy right now.",
    shallShowContactDetails: true,
  },
};

export function EndorsementIneligibleModal({
  isModalOpen,
  reason,
  onClose,
}: EndorsementIneligibleModalProps) {
  const message = reasonMessages[reason] ?? reasonMessages.Unknown;

  return (
    <Modal
      open={isModalOpen}
      onClose={onClose}
      title={message.title}
      maxWidth={false}
      sx={{
        width: { xs: "80%", md: 720 },
        "&:focus": {
          outline: "none",
        },
      }}>
      <Typography variant="h5" sx={{ pr: 5, textAlign: "justify" }}>
        {message.description}{" "}
        {message.shallShowContactDetails ? (
          <>
            Please contact{" "}
            <Link color="secondary" href={`mailto:${AGENT_HELP_ADDRESS}`}>
              {AGENT_HELP_ADDRESS}
            </Link>{" "}
            for assistance.
          </>
        ) : (
          <></>
        )}
      </Typography>
      <Box mt={4} columnGap={3} display="flex">
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Modal>
  );
}
